import { useMutation, useQuery } from '@tanstack/react-query'
import type { inferProcedureOutput } from '@trpc/server'
import { ActionCard, Button, Dialog, Input, Modal, Select, SpinnerWithBackground, Text } from '@viapeople/components'
import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { AppRouter } from '../../../../../api/src/api/index'
import type { TaskStatusEnum } from '../../../../../api/src/modules/tasks/tasks.types'
import { type Column, GenericTable } from '../../../components/GenericTable'
import { useToast } from '../../../utils/ToastContext'
import { queryClient, trpc } from '../../../utils/trpc'

type AdminTask = inferProcedureOutput<AppRouter['admin']['cycles']['tasks']['search']>['data'][number]
type AdminTaskSearchInput = {
	organizationId: string
	cycleId: string
	page: number
	perPage: number
	search?: string
	byStatus?: TaskStatusEnum[]
	byStepType?: string
	sortBy?: string
	order?: 'asc' | 'desc'
	includeRemoved?: boolean
}

export function CycleLaunchCycleTab() {
	const { organizationId, cycleId } = useParams<{ organizationId: string; cycleId: string }>()
	const [showConfirmModal, setShowConfirmModal] = useState(false)
	const [showWarningModal, setShowWarningModal] = useState(false)
	const [showCompleteModal, setShowCompleteModal] = useState(false)
	const [showReopenModal, setShowReopenModal] = useState(false)
	const [showUnsubmitModal, setShowUnsubmitModal] = useState(false)
	const [showInfoModal, setShowInfoModal] = useState(false)
	const [showEditOwnerModal, setShowEditOwnerModal] = useState(false)
	const [selectedTask, setSelectedTask] = useState<AdminTask | null>(null)
	const [deleteResponses, setDeleteResponses] = useState(false)
	const [statusFilter, setStatusFilter] = useState<string>('completed')
	const [newTaskOwnerId, setNewTaskOwnerId] = useState<string>('')
	const [ownerSearchQuery, setOwnerSearchQuery] = useState<string>('')
	const [isOwnerDropdownOpen, setIsOwnerDropdownOpen] = useState(false)
	const ownerDropdownRef = React.useRef<HTMLDivElement>(null)
	const { showToast } = useToast()

	const { data: cycle, isLoading: loadingCycle } = useQuery({
		...trpc.admin.cycles.getById.queryOptions({
			id: cycleId as string,
		}),
		enabled: !!cycleId,
	})

	const handleLaunchCycle = async () => {
		if (cycle?.status === 'ready-for-launch') {
			setShowConfirmModal(true)
		} else {
			setShowWarningModal(true)
		}
	}

	const launchCycleMutation = useMutation({
		...trpc.admin.cycles.launch.mutationOptions(),
		onSuccess: () => {
			showToast('Cycle launched successfully')
		},
		onError: (e) => {
			showToast(e.message ? e.message : 'Something went wrong, cycle was not launched', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.getById.queryKey({ id: cycleId }),
				exact: false,
				refetchType: 'all',
			})
			setShowConfirmModal(false)
		},
	})

	const confirmLaunchHandler = async () => {
		launchCycleMutation.mutate({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
		})
	}

	const unsubmitTaskMutation = useMutation({
		...trpc.admin.cycles.tasks.unsubmit.mutationOptions(),
		onSuccess: () => {
			showToast('Task unsubmitted successfully')
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.tasks.search.queryKey(),
				exact: false,
			})
			setShowUnsubmitModal(false)
			setSelectedTask(null)
		},
		onError: (e) => {
			showToast(e.message || 'Failed to unsubmit task', 'error')
		},
	})

	const updateTaskOwnerMutation = useMutation({
		...trpc.admin.cycles.tasks.updateOwner.mutationOptions(),
		onSuccess: () => {
			showToast('Task owner updated successfully')
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.tasks.search.queryKey(),
				exact: false,
			})
			setShowEditOwnerModal(false)
			setSelectedTask(null)
			setNewTaskOwnerId('')
		},
		onError: (e) => {
			showToast(e.message || 'Failed to update task owner', 'error')
		},
	})

	// Fetch users for task owner selection (lightweight: id, name, email only)
	const { data: allUsers } = useQuery({
		...trpc.admin.users.getForDropdown.queryOptions({
			organizationId: organizationId as string,
		}),
		enabled: !!organizationId && showEditOwnerModal,
	})

	const handleUnsubmit = (task: AdminTask) => {
		setSelectedTask(task)
		setDeleteResponses(false) // Reset checkbox state
		setShowUnsubmitModal(true)
	}

	const confirmUnsubmit = () => {
		if (!selectedTask?.id) {
			showToast('No task selected', 'error')
			return
		}

		unsubmitTaskMutation.mutate({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
			taskId: selectedTask.id,
			reason: 'Unsubmitted by admin',
			deleteResponses,
		})
	}

	const handleEditOwner = (task: AdminTask) => {
		setSelectedTask(task)
		setNewTaskOwnerId(task.taskOwnerId ?? '')
		setOwnerSearchQuery('')
		setIsOwnerDropdownOpen(false)
		setShowEditOwnerModal(true)
	}

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (ownerDropdownRef.current && !ownerDropdownRef.current.contains(event.target as Node)) {
				setIsOwnerDropdownOpen(false)
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [])

	const confirmUpdateOwner = () => {
		if (!selectedTask?.id) {
			showToast('No task selected', 'error')
			return
		}

		if (!newTaskOwnerId) {
			showToast('Please select a new owner', 'error')
			return
		}

		updateTaskOwnerMutation.mutate({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
			taskId: selectedTask.id,
			newOwnerId: newTaskOwnerId,
		})
	}

	const completeCycleMutation = useMutation({
		...trpc.admin.cycles.complete.mutationOptions(),
		onSuccess: () => {
			showToast('Cycle completed successfully')
		},
		onError: (e) => {
			showToast(e.message ? e.message : 'Something went wrong, cycle was not completed', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.getById.queryKey({ id: cycleId }),
				exact: false,
				refetchType: 'all',
			})
			setShowCompleteModal(false)
		},
	})

	const reopenCycleMutation = useMutation({
		...trpc.admin.cycles.reopen.mutationOptions(),
		onSuccess: () => {
			showToast('Cycle reopened successfully')
		},
		onError: (e) => {
			showToast(e.message ? e.message : 'Something went wrong, cycle was not reopened', 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.getById.queryKey({ id: cycleId }),
				exact: false,
				refetchType: 'all',
			})
			setShowReopenModal(false)
		},
	})

	const confirmCompleteHandler = () => {
		completeCycleMutation.mutate({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
		})
	}

	const confirmReopenHandler = () => {
		reopenCycleMutation.mutate({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
		})
	}

	const isBeyondLaunch = cycle && ['in-progress', 'completed'].includes(cycle.status)

	// Filter and sort users for the owner dropdown
	const filteredAndSortedUsers = useMemo(() => {
		if (!allUsers) return []

		let filtered = allUsers

		// Apply search filter
		if (ownerSearchQuery.trim()) {
			const query = ownerSearchQuery.toLowerCase()
			filtered = filtered.filter(
				(user) =>
					user.firstName.toLowerCase().includes(query) ||
					user.lastName.toLowerCase().includes(query) ||
					user.email.toLowerCase().includes(query),
			)
		}

		// Sort alphabetically by full name
		return filtered.sort((a, b) => {
			const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
			const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()
			return nameA.localeCompare(nameB)
		})
	}, [allUsers, ownerSearchQuery])

	const tasksColumns: Column<AdminTask>[] = [
		{
			field: 'taskOwnerFirstName',
			label: 'Task Owner',
			colSpan: 2,
			render: (row: AdminTask) => (
				<div className='flex items-center gap-2'>
					<span>
						{row.taskOwnerFirstName} {row.taskOwnerLastName}
					</span>
					<button
						type='button'
						onClick={() => handleEditOwner(row)}
						className='text-blue-600 hover:text-blue-800 focus:outline-none'
						title='Edit task owner'>
						<svg
							className='h-4 w-4'
							xmlns='http://www.w3.org/2000/svg'
							viewBox='0 0 20 20'
							fill='currentColor'
							aria-hidden='true'>
							<path d='M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z' />
							<path
								fillRule='evenodd'
								d='M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z'
								clipRule='evenodd'
							/>
						</svg>
					</button>
				</div>
			),
		},
		{
			field: 'participantFirstName',
			label: 'Self Evaluator',
			colSpan: 2,
			render: (row: AdminTask) => (
				<span>
					{row.participantFirstName} {row.participantLastName}
				</span>
			),
		},
		{
			field: 'cycleStepName',
			label: 'Step',
			colSpan: 1,
		},
		{
			field: 'status',
			label: 'Status',
			colSpan: 1,
			render: (row: AdminTask) => {
				const statusColors = {
					completed: 'bg-green-100 text-green-800',
					'in-progress': 'bg-blue-100 text-blue-800',
					'not-started': 'bg-gray-100 text-gray-800',
				}
				return (
					<span
						className={`px-2 py-1 rounded-full text-xs font-medium ${
							statusColors[row.status as keyof typeof statusColors]
						}`}>
						{row.status}
					</span>
				)
			},
		},
		{
			field: 'deadline',
			label: 'Deadline',
			colSpan: 1,
			render: (row: AdminTask) => (row.deadline ? new Date(row.deadline).toLocaleDateString() : '-'),
		},
		{
			field: 'updatedAt',
			label: 'Last Updated',
			colSpan: 1,
			render: (row: AdminTask) => {
				if (!row.updatedAt) return <div>-</div>
				const date = new Date(row.updatedAt)
				return (
					<div>
						<div>{date.toLocaleDateString()}</div>
						<div className='text-xs text-gray-500'>{date.toLocaleTimeString()}</div>
					</div>
				)
			},
		},
		{
			field: 'id',
			label: 'Actions',
			sortable: false,
			colSpan: 1,
			render: (row: AdminTask) => {
				const canUnsubmit = row.status === 'completed'

				return (
					<Button
						variant='secondary'
						size='small'
						onClick={() => handleUnsubmit(row)}
						disabled={!canUnsubmit}
						title={!canUnsubmit ? 'Only completed tasks can be unsubmitted' : undefined}>
						Unsubmit
					</Button>
				)
			},
		},
	]

	return (
		<div>
			<ActionCard
				title='Launch cycle'
				variant='primary'
				button={{
					children: launchCycleMutation.isPending ? 'Launching...' : 'Launch Cycle',
					size: 'medium',
					onClick: handleLaunchCycle,
					disabled: launchCycleMutation.isPending || isBeyondLaunch || loadingCycle,
					title: !loadingCycle && isBeyondLaunch ? `Cycle is ${cycle?.status}` : undefined,
				}}>
				<Text variant='xsmall' weight='regular' className='text-neutral-600'>
					Starts the cycle by sending it to all participants
				</Text>
			</ActionCard>

			<Dialog
				isOpen={showConfirmModal}
				onClose={() => setShowConfirmModal(false)}
				onConfirm={confirmLaunchHandler}
				title='Launch Cycle'
				message='Are you sure you want to launch this cycle?'
				type='confirm'
				confirmLabel='Launch'
			/>

			{cycle?.status === 'in-progress' && (
				<ActionCard
					title='Complete cycle'
					variant='secondary'
					button={{
						children: completeCycleMutation.isPending ? 'Completing...' : 'Complete Cycle',
						size: 'medium',
						onClick: () => setShowCompleteModal(true),
						disabled: completeCycleMutation.isPending || loadingCycle,
					}}>
					<Text variant='xsmall' weight='regular' className='text-neutral-600'>
						Marks the cycle as completed and stops all automated emails
					</Text>
				</ActionCard>
			)}

			{cycle?.status === 'completed' && (
				<ActionCard
					title='Reopen cycle'
					variant='secondary'
					button={{
						children: reopenCycleMutation.isPending ? 'Reopening...' : 'Reopen Cycle',
						size: 'medium',
						onClick: () => setShowReopenModal(true),
						disabled: reopenCycleMutation.isPending || loadingCycle,
					}}>
					<Text variant='xsmall' weight='regular' className='text-neutral-600'>
						Sets the cycle back to in-progress and resumes automated emails
					</Text>
				</ActionCard>
			)}

			<Dialog
				isOpen={showCompleteModal}
				onClose={() => setShowCompleteModal(false)}
				onConfirm={confirmCompleteHandler}
				title='Complete Cycle'
				message='Are you sure you want to complete this cycle? Automated emails will stop immediately.'
				type='confirm'
				confirmLabel='Complete'
			/>

			<Dialog
				isOpen={showReopenModal}
				onClose={() => setShowReopenModal(false)}
				onConfirm={confirmReopenHandler}
				title='Reopen Cycle'
				message='Are you sure you want to reopen this cycle? It will return to in-progress and automated emails will resume.'
				type='confirm'
				confirmLabel='Reopen'
			/>

			{launchCycleMutation.isPending && <SpinnerWithBackground />}

			<Dialog
				isOpen={showWarningModal}
				onClose={() => setShowWarningModal(false)}
				title='Cannot Launch Cycle'
				message="Build cycle before launching. A cycle can't be built without general info, admins, participants, roles, hierarchies, steps, and reviewers rules added."
				type='confirm'
			/>

			<Modal
				isOpen={showUnsubmitModal}
				onClose={() => {
					setShowUnsubmitModal(false)
					setSelectedTask(null)
				}}
				title='Unsubmit Task'>
				<div className='space-y-4'>
					<Text variant='small' className='text-gray-700'>
						Are you sure you want to unsubmit "{selectedTask?.cycleStepName}" by {selectedTask?.taskOwnerFirstName}{' '}
						{selectedTask?.taskOwnerLastName} (evaluating {selectedTask?.participantFirstName}{' '}
						{selectedTask?.participantLastName})?
					</Text>

					<Text variant='small' className='text-gray-700'>
						This will change the task status back to "In Progress". Any future-step tasks will be deleted.
					</Text>

					<div className='flex items-start gap-3 p-3 bg-gray-50 rounded border border-gray-200'>
						<input
							type='checkbox'
							id='deleteResponses'
							checked={deleteResponses}
							onChange={(e) => setDeleteResponses(e.target.checked)}
							className='mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded'
						/>
						<label htmlFor='deleteResponses' className='flex-1 cursor-pointer'>
							<Text variant='small' weight='semibold' className='text-gray-900'>
								Delete all form responses
							</Text>
							<Text variant='xsmall' className='text-gray-600 mt-1'>
								Check this to clear all responses and force the evaluator to start from scratch. Leave unchecked to
								preserve their existing responses.
							</Text>
						</label>
					</div>

					<div className='flex justify-end gap-3 pt-4'>
						<Button
							variant='secondary'
							onClick={() => {
								setShowUnsubmitModal(false)
								setSelectedTask(null)
							}}>
							Cancel
						</Button>
						<Button variant='primary' onClick={confirmUnsubmit}>
							Unsubmit Task
						</Button>
					</div>
				</div>
			</Modal>

			<Modal
				isOpen={showEditOwnerModal}
				onClose={() => {
					setShowEditOwnerModal(false)
					setSelectedTask(null)
					setNewTaskOwnerId('')
					setOwnerSearchQuery('')
					setIsOwnerDropdownOpen(false)
				}}
				title='Edit Task Owner'>
				<div className='space-y-4'>
					<Text variant='small' className='text-gray-700'>
						Change the owner of "{selectedTask?.cycleStepName}" for {selectedTask?.participantFirstName}{' '}
						{selectedTask?.participantLastName}
					</Text>

					<div>
						<Text variant='xsmall' weight='semibold' className='mb-2'>
							Current Owner: {selectedTask?.taskOwnerFirstName} {selectedTask?.taskOwnerLastName}
						</Text>

						<Text variant='xsmall' weight='semibold' className='mb-1'>
							New Owner <span className='text-red-500'>*</span>
						</Text>

						{/* Custom searchable dropdown */}
						<div className='relative' ref={ownerDropdownRef}>
							<button
								type='button'
								onClick={() => setIsOwnerDropdownOpen(!isOwnerDropdownOpen)}
								className='w-full px-4 py-2 text-left bg-white border border-neutral-300 rounded-sm hover:border-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-h-[44px] flex items-center justify-between'>
								<span className='truncate'>
									{newTaskOwnerId
										? (() => {
												const selectedUser = allUsers?.find((u) => u.id === newTaskOwnerId)
												return selectedUser
													? `${selectedUser.firstName} ${selectedUser.lastName} (${selectedUser.email})`
													: 'Select a new owner...'
											})()
										: 'Select a new owner...'}
								</span>
								<svg
									className={`w-5 h-5 transition-transform ${isOwnerDropdownOpen ? 'transform rotate-180' : ''}`}
									fill='none'
									stroke='currentColor'
									viewBox='0 0 24 24'
									aria-hidden='true'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
								</svg>
							</button>

							{isOwnerDropdownOpen && (
								<div className='absolute z-50 w-full mt-1 bg-white border border-neutral-300 rounded-sm shadow-lg max-h-80 overflow-hidden'>
									{/* Search input inside dropdown */}
									<div className='p-2 border-b border-neutral-200 sticky top-0 bg-white'>
										<Input
											placeholder='Search users by name or email...'
											value={ownerSearchQuery}
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOwnerSearchQuery(e.target.value)}
											className='w-full'
											autoFocus
										/>
									</div>

									{/* Options list */}
									<div className='overflow-y-auto max-h-64'>
										{filteredAndSortedUsers.length > 0 ? (
											filteredAndSortedUsers.map((user) => (
												<button
													key={user.id}
													type='button'
													onClick={() => {
														setNewTaskOwnerId(user.id)
														setIsOwnerDropdownOpen(false)
														setOwnerSearchQuery('')
													}}
													className={`w-full px-4 py-2 text-left hover:bg-neutral-100 focus:outline-none focus:bg-neutral-100 ${
														newTaskOwnerId === user.id ? 'bg-primary-50' : ''
													}`}>
													<div className='text-sm'>
														{user.firstName} {user.lastName}
													</div>
													<div className='text-xs text-gray-500'>{user.email}</div>
												</button>
											))
										) : (
											<div className='px-4 py-3 text-sm text-gray-500 text-center'>
												{ownerSearchQuery ? `No users found matching "${ownerSearchQuery}"` : 'No users available'}
											</div>
										)}
									</div>
								</div>
							)}
						</div>
					</div>

					<div className='p-3 bg-blue-50 rounded'>
						<Text variant='xsmall' className='text-blue-700'>
							💡 This will reassign the task to the selected user. The task status and any existing progress will remain
							unchanged.
						</Text>
					</div>

					<div className='flex justify-end gap-3 pt-4'>
						<Button
							variant='secondary'
							onClick={() => {
								setShowEditOwnerModal(false)
								setSelectedTask(null)
								setNewTaskOwnerId('')
								setOwnerSearchQuery('')
								setIsOwnerDropdownOpen(false)
							}}>
							Cancel
						</Button>
						<Button
							variant='primary'
							onClick={confirmUpdateOwner}
							disabled={!newTaskOwnerId || updateTaskOwnerMutation.isPending}>
							{updateTaskOwnerMutation.isPending ? 'Updating...' : 'Update Owner'}
						</Button>
					</div>
				</div>
			</Modal>

			<Modal isOpen={showInfoModal} onClose={() => setShowInfoModal(false)} title='How Unsubmit Works'>
				<div className='space-y-4 text-sm'>
					<div>
						<Text variant='small' weight='semibold' className='mb-2'>
							What happens when you unsubmit a task:
						</Text>
						<ul className='list-disc list-inside space-y-1 text-gray-700'>
							<li>The completed task status changes back to "In Progress"</li>
							<li>The same evaluator keeps their assignment</li>
							<li>
								<strong>All future-step tasks are deleted</strong> to prevent workflow issues
							</li>
							<li>The participant's evaluation moves back to that step</li>
							<li>Optionally, you can delete all form responses to force a fresh start</li>
						</ul>
					</div>

					<div className='p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded'>
						<div className='flex'>
							<div className='flex-shrink-0'>
								<svg
									className='h-5 w-5 text-yellow-400'
									xmlns='http://www.w3.org/2000/svg'
									viewBox='0 0 20 20'
									fill='currentColor'
									aria-hidden='true'>
									<path
										fillRule='evenodd'
										d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
										clipRule='evenodd'
									/>
								</svg>
							</div>
							<div className='ml-3'>
								<Text variant='small' weight='semibold' className='text-yellow-800'>
									⚠️ Important: Unsubmit in Reverse Order
								</Text>
								<Text variant='xsmall' className='text-yellow-700 mt-1'>
									You MUST unsubmit tasks in reverse chronological order (most recent step first, then work backwards).
									Unsubmitting an earlier step will delete all tasks from later steps, potentially removing work that
									needs to be unsubmitted separately.
								</Text>
							</div>
						</div>
					</div>

					<div>
						<Text variant='small' weight='semibold' className='mb-2'>
							Why we delete future-step tasks:
						</Text>
						<Text variant='xsmall' className='text-gray-700'>
							The system shows all non-stale tasks to users regardless of their current step. If we kept future-step
							tasks, participants could access and complete tasks from steps they haven't reached yet, corrupting the
							evaluation workflow.
						</Text>
					</div>

					<div className='p-3 bg-blue-50 rounded'>
						<Text variant='small' weight='semibold' className='text-blue-800 mb-1'>
							💡 Best Practice
						</Text>
						<Text variant='xsmall' className='text-blue-700'>
							Sort by "Last Updated" (descending) to see the most recent tasks first. This makes it easier to unsubmit
							in the correct order.
						</Text>
					</div>
				</div>
			</Modal>

			{isBeyondLaunch && (
				<div className='pt-6'>
					<div className='mb-6'>
						<div className='flex items-center justify-between mb-4'>
							<div className='flex items-center gap-2'>
								<Text variant='medium' weight='semibold' className='text-sm'>
									Cycle Tasks
								</Text>
								<button
									type='button'
									onClick={() => setShowInfoModal(true)}
									className='text-yellow-600 hover:text-yellow-700 focus:outline-none cursor-pointer'
									title='Important information about unsubmitting tasks'>
									<svg
										className='h-4 w-4'
										xmlns='http://www.w3.org/2000/svg'
										viewBox='0 0 20 20'
										fill='currentColor'
										aria-hidden='true'>
										<path
											fillRule='evenodd'
											d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
											clipRule='evenodd'
										/>
									</svg>
								</button>
							</div>
							<div className='flex gap-2'>
								<Select
									value={statusFilter}
									onChange={(value: string) => setStatusFilter(value)}
									options={[
										{ value: 'all', label: 'All Status' },
										{ value: 'completed', label: 'Completed' },
										{ value: 'in-progress', label: 'In Progress' },
										{ value: 'not-started', label: 'Not Started' },
										{ value: 'declined', label: 'Declined' },
										{ value: 'rejected', label: 'Rejected' },
									]}
								/>
							</div>
						</div>
						<div className='mt-4 mb-8'>
							<GenericTable<AdminTask, AdminTaskSearchInput>
								queryOptionsGenerator={(options) => {
									const queryOptions: AdminTaskSearchInput = {
										...options,
										organizationId: organizationId as string,
										cycleId: cycleId || '',
										sortBy: options.sortBy || 'updatedAt',
										order: options.order || 'desc',
										includeRemoved: true,
									}

									// Only add byStatus if not 'all'
									if (statusFilter !== 'all') {
										queryOptions.byStatus = [statusFilter as TaskStatusEnum]
									}

									return {
										...trpc.admin.cycles.tasks.search.queryOptions(queryOptions),
										enabled: !!cycleId && !!organizationId,
									}
								}}
								columns={tasksColumns}
							/>
						</div>
					</div>
				</div>
			)}
		</div>
	)
}
